import { useContext } from "react";

import { NamePath } from "antd/lib/form/interface";
import { FieldContext } from "rc-field-form";

function toArray(name: NamePath): Array<string | number> {
    return Array.isArray(name) ? name : [name];
}

/**
 * This hook simplifies working with reusable custom form components
 * by providing the absolute path to a given form item (mainly when used in lists).
 * `antD` will automatically prefix the names of form items when used inside a `<Form.List />`
 * which is creating a subtle problem:
 * 1. the `name` property of the `<Form.Item name={...} />` must **not** include that prefix (declarative)
 * 2. if we imperatively call any methods on the `form` instance (ex. `form.getFieldValue([...name])`), we **must** include the prefix
 *
 * This creates a problem when the `name` we give to a form item can't be used to trigger validations, get or set the value on that item.
 * In order to fix it, when we need the full name, we can call the `getFieldName` method exposed below, which adds the prefix to the given name.
 * (The prefix is available in the context created by antd).
 */
export function useFormInput() {
    const { prefixName } = useContext(FieldContext);

    const getFieldName = (name: NamePath) => {
        if (!prefixName) {
            return name;
        }

        return [...prefixName, ...toArray(name)];
    };

    return {
        prefixName,
        getFieldName,
    };
}
